/**
 * @description: 上传组件
 */

import React, { forwardRef, useImperativeHandle } from 'react';
import $i18n from 'panda-i18n';
import { Box, Button, Icon, Upload } from '@fpxfd/next';
import styles from './index.module.scss';
import { UploadProps } from '@fpxfd/next/types/upload';
import uploadIcon from '@/assets/images/base/upload.png';
import { useSetState } from 'ahooks';
import { fileProps, StatesProps } from './index.interface';
import Dragger2 from './Dragger2';
import { getLocale } from '@/locales/locale';
import { getWarehouseCode, isPromise } from '@/utils';
import { GATEWAY_URL } from '@/config';
import { BASEDATASERVICE } from '@/const';

interface FpxUploadProps extends UploadProps {
  /**
   * 拖拽类型
   */
  type?: number;
  /**
   * 拖曳区域文本内容
   */
  dragText?: React.ReactNode;
}

const FpxUpload = forwardRef(({ dragable = false, type = 1, dragText = null, ...lastprops }: FpxUploadProps, ref) => {
  const [states, setStates] = useSetState<StatesProps>({
    percent: 0,
    fileName: '',
    status: 'init',
    resultDesc: '',
  });

  useImperativeHandle(ref, () => ({
    setStatus,
  }));

  // drag type=2样式处理
  const setStatus = (status: string, fileName = '') => {
    setStates({
      status,
      fileName,
    });
  };

  const beforeUpload = (file: Object, options: Object) => {
    setStates({
      status: 'init',
    });
    if (lastprops?.beforeUpload) {
      const res = lastprops.beforeUpload(file, options);
      if (isPromise(res)) {
        return res.catch(() => {
          return false;
        });
      }
    }
  };
  const progress = (step) => {
    setStates({
      status: 'progress',
      percent: step[0].percent,
    });

    lastprops?.onProgress && lastprops.onProgress(step);
  };
  const success = (file: fileProps, value: any[]) => {
    if (file?.response?.success) {
      setStates({
        status: 'success',
        fileName: file.name,
      });

      lastprops?.onSuccess && lastprops.onSuccess(file, value);
    } else {
      error(file, value);
    }
  };
  const error = (file: fileProps, value: any[]) => {
    setStates({
      status: 'error',
      fileName: file.name,
    });
    console.log('file');
    console.log(file);

    lastprops?.onError && lastprops.onError(file, value);
  };

  const dragableContent = (
    <Box className={styles.next_upload_drag} justify="center" align="center" spacing={14}>
      <div>
        <Icon type="add" style={{ color: '#A9AEBA' }} />
      </div>
      <span className={styles.next_upload_drag_text}>
        {dragText || $i18n.get({ id: 'ClickOrDragToUploadHere_144213775', dm: '点击或者拖拽至此上传' })}
      </span>
    </Box>
  );

  const dragableContent_1 = (
    <Box className={styles.next_upload_drag_1} justify="center" align="center" spacing={14}>
      <div>
        <img src={uploadIcon} />
      </div>
      <span className={styles.next_upload_drag_text_1}>
        {dragText || $i18n.get({ id: 'SupportDragAndDropAndClick_87867791', dm: '支持拖拽与点击上传' })}
      </span>
      <Button type="secondary">{$i18n.get({ id: 'SelectAFile.four_px', dm: '选择文件' })}</Button>
    </Box>
  );

  const normalContent = (
    <Button style={{ marginBottom: 8 }}>
      <Icon type="add" />
      {$i18n.get({ id: 'UploadObjects.four_px', dm: '上传文件' })}
    </Button>
  );

  const actionUrl = `${GATEWAY_URL}${BASEDATASERVICE}/system/upload/file`;

  const drageClass = type === 1 ? styles.upload_dragable_box : styles.upload_dragable_box_big;

  return dragable ? (
    <div className={`${lastprops.className} ${dragable ? drageClass : ''}`}>
      <Upload.Dragger
        action={actionUrl}
        // withCredentials={false}
        listType="image"
        fileKeyName="file"
        headers={{
          SESSION: window.localStorage.getItem('session'),
          WAREHOUSE: getWarehouseCode(),
          'Accept-Language': getLocale() === 'zh-CN' ? 'zh-CN,zh' : 'en-US,en',
        }}
        {...lastprops}
        beforeUpload={beforeUpload}
        onProgress={progress}
        onSuccess={success}
        onError={error}
        className={`${lastprops.className} ${dragable ? drageClass : ''}`}
        formatter={(res, file) => {
          // 函数里面根据当前服务器返回的响应数据
          // 重新拼装符合组件要求的数据格式
          return {
            success: res.result === '1',
            url: res.data,
            data: res.data,
            errors: res?.errors,
          };
        }}
      >
        {lastprops.children ||
          (type === 1 ? dragableContent : <Dragger2 states={states} content={dragableContent_1} />)}
      </Upload.Dragger>
    </div>
  ) : (
    <Upload
      action={actionUrl}
      // withCredentials={false}
      listType="text"
      fileKeyName="file"
      headers={{
        SESSION: window.localStorage.getItem('session'),
        WAREHOUSE: getWarehouseCode(),
        'Accept-Language': getLocale() === 'zh-CN' ? 'zh-CN,zh' : 'en-US,en',
      }}
      formatter={(res, file) => {
        // 函数里面根据当前服务器返回的响应数据
        // 重新拼装符合组件要求的数据格式
        if (res.result === '1') {
          return {
            success: res.result === '1',
            url: res.data,
            data: res.data,
            errors: res?.errors,
          };
        }
        return res;
      }}
      {...lastprops}
    >
      {lastprops.children || normalContent}
    </Upload>
  );
});

export default FpxUpload;
